if (!customElements.get('ly-design-journey')) {
    customElements.define('ly-design-journey', class extends HTMLElement {
        currentIndex = 0
        touchStart = null

        connectedCallback() {
            this.stepButtons = [...this.querySelectorAll('[data-step-index]')]
            this.panels = [...this.querySelectorAll('[data-panel]')]
            this.stepperScroller = this.querySelector('[data-stepper-scroller]')
            this.panelsWrapper = this.querySelector('[data-panels]')

            if (!this.stepButtons.length || !this.panels.length) return

            const selectedIndex = this.stepButtons.findIndex(button => button.getAttribute('aria-selected') === 'true')
            this.currentIndex = selectedIndex < 0 ? 0 : selectedIndex

            this.addEventListener('click', this.handleClick)
            this.addEventListener('keydown', this.handleKeydown)
            this.panelsWrapper?.addEventListener('touchstart', this.handleTouchStart, { passive: true })
            this.panelsWrapper?.addEventListener('touchmove', this.handleTouchMove, { passive: false })
            this.panelsWrapper?.addEventListener('touchend', this.handleTouchEnd, { passive: true })
            this.panelsWrapper?.addEventListener('touchcancel', this.handleTouchCancel)
            document.addEventListener('shopify:block:select', this.handleBlockSelect)

            this.select(this.currentIndex, { animate: false })
        }

        disconnectedCallback() {
            this.removeEventListener('click', this.handleClick)
            this.removeEventListener('keydown', this.handleKeydown)
            this.panelsWrapper?.removeEventListener('touchstart', this.handleTouchStart)
            this.panelsWrapper?.removeEventListener('touchmove', this.handleTouchMove)
            this.panelsWrapper?.removeEventListener('touchend', this.handleTouchEnd)
            this.panelsWrapper?.removeEventListener('touchcancel', this.handleTouchCancel)
            document.removeEventListener('shopify:block:select', this.handleBlockSelect)
        }

        handleClick = (event) => {
            const stepButton = event.target.closest('[data-step-index]')
            if (stepButton && this.contains(stepButton)) {
                this.select(Number(stepButton.dataset.stepIndex))
                return
            }

            const navigationButton = event.target.closest('[data-direction]')
            if (!navigationButton || !this.contains(navigationButton) || navigationButton.disabled) return

            const offset = navigationButton.dataset.direction === 'next' ? 1 : -1
            this.select(this.currentIndex + offset)
        }

        handleKeydown = (event) => {
            const stepButton = event.target.closest('[data-step-index]')
            if (!stepButton || !this.contains(stepButton)) return

            const index = Number(stepButton.dataset.stepIndex)
            let targetIndex = null

            if (event.key === 'Home') targetIndex = 0
            if (event.key === 'End') targetIndex = this.stepButtons.length - 1
            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                targetIndex = Math.min(index + 1, this.stepButtons.length - 1)
            }
            if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                targetIndex = Math.max(index - 1, 0)
            }

            if (targetIndex === null) return

            event.preventDefault()
            this.select(targetIndex, { focus: true })
        }

        handleTouchStart = (event) => {
            const touch = event.touches[0]
            this.touchStart = { x: touch.clientX, y: touch.clientY }
        }

        handleTouchMove = (event) => {
            if (!this.touchStart) return

            const touch = event.touches[0]
            const distanceX = touch.clientX - this.touchStart.x
            const distanceY = touch.clientY - this.touchStart.y

            if (Math.abs(distanceX) > 8 && Math.abs(distanceX) > Math.abs(distanceY)) {
                event.preventDefault()
            }
        }

        handleTouchCancel = () => {
            this.touchStart = null
        }

        handleTouchEnd = (event) => {
            if (!this.touchStart) return

            const touch = event.changedTouches[0]
            const distanceX = touch.clientX - this.touchStart.x
            const distanceY = touch.clientY - this.touchStart.y

            if (Math.abs(distanceX) > 50 && Math.abs(distanceX) > Math.abs(distanceY) * 1.25) {
                this.select(this.currentIndex + (distanceX < 0 ? 1 : -1))
            }

            this.touchStart = null
        }

        handleBlockSelect = (event) => {
            if (!event.detail?.blockId) return

            const selectedStep = this.stepButtons.find(button => button.dataset.blockId === event.detail.blockId)
            if (!selectedStep) return

            this.select(Number(selectedStep.dataset.stepIndex))
        }

        select(index, { animate = true, focus = false } = {}) {
            if (index < 0 || index >= this.panels.length) return

            const previousIndex = this.currentIndex
            const direction = index >= previousIndex ? 'next' : 'previous'
            this.currentIndex = index

            this.stepButtons.forEach((button, buttonIndex) => {
                const isActive = buttonIndex === index

                button.classList.toggle('is-active', isActive)
                button.classList.toggle('is-complete', buttonIndex < index)
                button.setAttribute('aria-selected', isActive ? 'true' : 'false')
                button.tabIndex = isActive ? 0 : -1
            })

            this.panels.forEach((panel, panelIndex) => {
                const isActive = panelIndex === index
                panel.hidden = !isActive

                if (isActive && animate && index !== previousIndex) {
                    panel.dataset.direction = direction
                } else {
                    delete panel.dataset.direction
                }
            })

            const activeButton = this.stepButtons[index]
            if (focus) activeButton.focus()
            this.scrollStepIntoView(activeButton)

            this.dispatchEvent(new CustomEvent('ly:design-journey:change', {
                bubbles: true,
                detail: { index }
            }))
        }

        scrollStepIntoView(button) {
            if (!this.stepperScroller || this.stepperScroller.scrollWidth <= this.stepperScroller.clientWidth) return

            const left = button.offsetLeft - (this.stepperScroller.clientWidth - button.offsetWidth) / 2
            this.stepperScroller.scrollTo({ left, behavior: 'smooth' })
        }
    })
}
